import { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, DeviceMobile, Envelope, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function SecuritySettingsCard() {
    const { data, mutate } = useSWR("/auth/2fa/status", fetcher);
    const [totpSetup, setTotpSetup] = useState(null);   // {secret, otpauth_uri, qr_png_b64}
    const [totpCode, setTotpCode] = useState("");
    const [activating, setActivating] = useState(false);
    const [disablingPassword, setDisablingPassword] = useState("");
    const [showDisable, setShowDisable] = useState(null); // "totp" | "email"

    const startTotpSetup = async () => {
        try {
            const { data: r } = await api.post("/auth/2fa/totp/setup");
            setTotpSetup(r);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec de la génération du QR code");
        }
    };

    const activateTotp = async () => {
        if (!totpCode.trim()) return;
        setActivating(true);
        try {
            await api.post("/auth/2fa/totp/activate", { code: totpCode });
            toast.success("Authentification TOTP activée ✓");
            setTotpSetup(null);
            setTotpCode("");
            mutate();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Code incorrect");
        } finally {
            setActivating(false);
        }
    };

    const enableEmail = async () => {
        try {
            await api.post("/auth/2fa/email/enable");
            toast.success("Authentification par email activée ✓");
            mutate();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec — vérifiez votre config SMTP");
        }
    };

    const doDisable = async () => {
        if (!disablingPassword) return;
        try {
            await api.post("/auth/2fa/disable", { password: disablingPassword, method: showDisable });
            toast.success("Méthode 2FA désactivée");
            setShowDisable(null);
            setDisablingPassword("");
            mutate();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Désactivation impossible");
        }
    };

    const totpOn = !!data?.totp_enabled;
    const emailOn = !!data?.email_2fa_enabled;

    return (
        <Card className="ec-card" data-testid="security-settings-card">
            <CardContent className="p-6 space-y-5">
                <div className="flex items-start gap-3">
                    <ShieldCheck size={22} className="text-primary mt-0.5" />
                    <div className="flex-1">
                        <h2 className="font-display text-lg font-bold tracking-tight">
                            Sécurité — Authentification à deux facteurs (2FA)
                        </h2>
                        <p className="text-sm text-muted-foreground mt-1">
                            Renforcez la sécurité de votre compte en ajoutant un second facteur de connexion.
                            Vous pouvez activer une ou les deux méthodes — à la connexion vous choisirez laquelle utiliser.
                        </p>
                    </div>
                </div>

                {/* TOTP */}
                <div className="rounded-md border border-border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <DeviceMobile size={18} className="text-primary" />
                            <h3 className="font-display font-bold">Application d&apos;authentification (TOTP)</h3>
                            {totpOn && (
                                <Badge variant="outline" className="text-emerald-700 border-emerald-200 ml-1">
                                    <CheckCircle size={12} weight="fill" className="mr-1" /> Active
                                </Badge>
                            )}
                        </div>
                        {totpOn ? (
                            <Button size="sm" variant="outline" onClick={() => setShowDisable("totp")} data-testid="disable-totp">
                                Désactiver
                            </Button>
                        ) : (
                            !totpSetup && (
                                <Button size="sm" onClick={startTotpSetup} data-testid="setup-totp">
                                    Configurer
                                </Button>
                            )
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Compatible avec Google Authenticator, 1Password, Authy, Microsoft Authenticator. Génère un code à 6 chiffres qui change toutes les 30 secondes.
                    </p>

                    {totpSetup && !totpOn && (
                        <div className="space-y-3 pt-2 border-t border-border">
                            <div className="flex flex-col md:flex-row gap-4 items-start">
                                <img
                                    src={`data:image/png;base64,${totpSetup.qr_png_b64}`}
                                    alt="QR code TOTP"
                                    className="w-40 h-40 border border-border rounded-md"
                                    data-testid="totp-qr"
                                />
                                <div className="flex-1 space-y-2">
                                    <p className="text-sm">
                                        <strong>1.</strong> Scannez ce QR code avec votre application d&apos;authentification.
                                    </p>
                                    <p className="text-sm">
                                        <strong>2.</strong> Si vous ne pouvez pas scanner, saisissez ce code manuellement :
                                    </p>
                                    <code className="block text-xs bg-muted/50 p-2 rounded font-mono break-all" data-testid="totp-secret">
                                        {totpSetup.secret}
                                    </code>
                                    <p className="text-sm">
                                        <strong>3.</strong> Entrez le code à 6 chiffres affiché dans l&apos;app pour confirmer :
                                    </p>
                                    <div className="flex gap-2">
                                        <Input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={totpCode}
                                            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                                            placeholder="123456"
                                            className="font-mono tabular text-lg w-32"
                                            autoFocus
                                            data-testid="totp-activate-code"
                                        />
                                        <Button
                                            onClick={activateTotp}
                                            disabled={activating || totpCode.length !== 6}
                                            data-testid="totp-activate-btn"
                                        >
                                            {activating ? "Vérification…" : "Activer"}
                                        </Button>
                                        <Button variant="ghost" onClick={() => { setTotpSetup(null); setTotpCode(""); }}>
                                            <X size={16} />
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Email OTP */}
                <div className="rounded-md border border-border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Envelope size={18} className="text-primary" />
                            <h3 className="font-display font-bold">Code par email</h3>
                            {emailOn && (
                                <Badge variant="outline" className="text-emerald-700 border-emerald-200 ml-1">
                                    <CheckCircle size={12} weight="fill" className="mr-1" /> Active
                                </Badge>
                            )}
                        </div>
                        {emailOn ? (
                            <Button size="sm" variant="outline" onClick={() => setShowDisable("email")} data-testid="disable-email-2fa">
                                Désactiver
                            </Button>
                        ) : (
                            <Button size="sm" onClick={enableEmail} data-testid="enable-email-2fa">
                                Activer
                            </Button>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Un code à 6 chiffres vous sera envoyé sur votre adresse email à chaque connexion. Nécessite une configuration SMTP valide.
                    </p>
                </div>

                {/* Disable confirmation */}
                {showDisable && (
                    <div className="rounded-md border-2 border-destructive/40 bg-destructive/5 p-4 space-y-3">
                        <h3 className="font-display font-bold text-destructive">
                            Désactiver la {showDisable === "totp" ? "2FA par application" : "2FA par email"}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                            Confirmez votre mot de passe pour désactiver cette méthode :
                        </p>
                        <div className="flex gap-2">
                            <Input
                                type="password"
                                value={disablingPassword}
                                onChange={(e) => setDisablingPassword(e.target.value)}
                                placeholder="Mot de passe"
                                autoFocus
                                onKeyDown={(e) => e.key === "Enter" && doDisable()}
                                data-testid="disable-password"
                            />
                            <Button variant="destructive" onClick={doDisable} data-testid="disable-confirm">
                                Désactiver
                            </Button>
                            <Button variant="ghost" onClick={() => { setShowDisable(null); setDisablingPassword(""); }}>
                                Annuler
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
